import { useEffect, useMemo } from 'react';
import { useAuthWrapper } from '@weareopenr/authkit';
import { IMenuSet } from 'src/api/getMenuSets';
import { FEATURE_FLAGS } from 'src/constants';
import { useMenuSets } from '../useMenuSets';

/**
 * Every menu set for the brand, for timetables to offer alongside menus. Pickers filter client-side
 * (by range, by the current default), so every page is fetched rather than one at a time.
 * Brands without the menu sets feature get none, so timetables only ever offer menus to them.
 *
 * If a page fails, fetching stops (useMenuSets already shows an error) and `isError` is set, so
 * callers know the list may be partial rather than treating it as complete.
 */
export const useTimetableMenuSets = (): { data: IMenuSet[]; isLoading: boolean; isError: boolean } => {
  const { featureFlags } = useAuthWrapper();
  const isEnabled = featureFlags.includes(FEATURE_FLAGS.MENU_SETS);
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useMenuSets(
    50,
    undefined,
    undefined,
    undefined,
    { enabled: isEnabled },
  );

  useEffect(() => {
    if (isEnabled && !isError && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [isEnabled, isError, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Gated on the flag too, not just the query - a menu set list cached elsewhere in the app would
  // otherwise still reach a brand without the feature.
  const menuSets = useMemo(() => (isEnabled ? data?.pages.flatMap((page) => page.data) ?? [] : []), [isEnabled, data]);

  return {
    data: menuSets,
    // A disabled query reports isLoading until it first runs, which it never will here.
    isLoading: isEnabled && !isError && (isLoading || hasNextPage === true),
    isError: isEnabled && isError,
  };
};
